/**
 * Wire types and mappers for the server-backed keyword pins API
 * (`user/coffee/customizations/keywordPins`, see Real-Magic/Levitate-API#11465).
 *
 * The endpoint serializes with System.Text.Json via the API's
 * `LevitateOutputFormatterSelector`, which builds its options from
 * `new JsonOptions()` — i.e. `JsonSerializerDefaults.Web`, so the wire shape is
 * **camelCase** (NOT PascalCase, despite the PR description). A `_type`
 * discriminator (literally `_type`) is emitted on read responses. Input is
 * case-insensitive (`PropertyNameCaseInsensitive = true`), but we send camelCase
 * for consistency.
 */
import type { KeywordPin, PinTrigger } from './types';

/** The `_type` discriminator token for the keyword-pins customization family. */
export const KEYWORD_PINS_TYPE = 'keywordPins';

/**
 * A keyword-pin row as returned by the API (camelCase, mirroring the server's
 * `CoffeeKeywordPinView`). `_type` is present on GET responses; it is not
 * required on POST/PUT bodies (those bind to the concrete view server-side).
 *
 * `builtin` / `userEdited` are nullable on the wire and omitted when null
 * (`DefaultIgnoreCondition = WhenWritingNull`) — a custom pin has neither; an
 * override of a built-in carries `builtin: true`.
 */
export type KeywordPinView = {
	_type?: string;
	id: string;
	position: number;
	label: string;
	keyword: string;
	color: string;
	prompt: string;
	trigger: string;
	enabled: boolean;
	builtin?: boolean | null;
	userEdited?: boolean | null;
};

/**
 * The POST/PUT body sent to the API (camelCase; server-owned fields like
 * id/position/creationDate are assigned server-side and omitted here). A custom
 * pin sends `builtin: false`; an override of a built-in sends `builtin: true`
 * plus `userEdited` (true for a content edit, false for a plain disable toggle).
 */
export type WritePinBody = {
	label: string;
	keyword: string;
	color: string;
	prompt: string;
	trigger: string;
	enabled: boolean;
	builtin: boolean;
	userEdited?: boolean;
};

const KNOWN_TRIGGERS: ReadonlyArray<PinTrigger> = ['before_call', 'on_dial', 'starter'];

/**
 * Coerce a free-form server `trigger` string into the FE `PinTrigger` union.
 * Unknown/empty values fall back to `'starter'` — the safe, non-auto-firing
 * mode (a misread trigger should never auto-send a prompt).
 */
export function coerceTrigger(trigger: string): PinTrigger {
	return (KNOWN_TRIGGERS as ReadonlyArray<string>).includes(trigger) ? (trigger as PinTrigger) : 'starter';
}

/**
 * Map a server row to a UI `KeywordPin`. `builtin`/`userEdited` are only set
 * when truthy so that custom rows leave them `undefined` and the merge step
 * treats them as custom (not built-in overrides).
 *
 * `position` is intentionally dropped — the GET list arrives position-sorted,
 * so callers preserve array order rather than carrying a position field.
 */
export function toKeywordPin(view: KeywordPinView): KeywordPin {
	return {
		id: view.id,
		label: view.label,
		keyword: view.keyword,
		color: view.color,
		prompt: view.prompt,
		trigger: coerceTrigger(view.trigger),
		enabled: view.enabled,
		...(view.builtin ? { builtin: true } : {}),
		...(view.userEdited ? { userEdited: true } : {}),
	};
}
